import { useMemo } from "react";

import {
  buildCoachAiPageContextData,
  buildCoachVeeraSignalInterpretPrompt,
  buildCoachVeeraSignalInterpretRoute,
  type CoachOverviewSignalType,
} from "@/lib/behavior/viewModels/buildCoachVeeraEntry";
import {
  buildCoachDigitalTwinRuntimeFromSources,
} from "@/lib/behavior/coachRuntime";
import { buildCoachRuntimeSurfaceViewModel } from "@/lib/behavior/viewModels/buildCoachRuntimeSurfaceViewModel";
import { useCoachRuntimeContext } from "@/hooks/useCoachRuntimeContext";
import type { CoachRuntimeContextType } from "@/lib/behavior/behaviorContracts";

type ActiveLeaderContext = {
  name: string;
  behaviorName: string | null;
  phase: string | null;
};

export function useCoachPortfolioRuntime(params: {
  coachId?: string;
  leaderId?: string | null;
  contextType: CoachRuntimeContextType;
  activeLeader?: ActiveLeaderContext | null;
  enabled?: boolean;
}) {
  const {
    coachId,
    leaderId = null,
    contextType,
    activeLeader = null,
    enabled = true,
  } = params;

  const { coachLens, coachTwin, coachMemory, isFetched, isError } = useCoachRuntimeContext({
    coachId,
    leaderId,
    enabled,
  });

  const coachRuntime = useMemo(
    () =>
      buildCoachDigitalTwinRuntimeFromSources({
        coachLens,
        coachTwin,
        contextType,
      }),
    [coachLens, coachTwin, contextType],
  );

  const coachRuntimeSurface = useMemo(
    () => buildCoachRuntimeSurfaceViewModel(coachRuntime),
    [coachRuntime],
  );

  const coachAiPageContextData = useMemo(
    () =>
      buildCoachAiPageContextData({
        activeLeader,
        coachRuntime,
        coachMemory,
      }),
    [activeLeader, coachMemory, coachRuntime],
  );

  const coachRuntimeActions = useMemo(
    () => ({
      buildSignalInterpretRoute(params: {
        signalType: CoachOverviewSignalType;
        signalLevel: "low" | "medium" | "high";
        leaderId?: string | null;
        signals?: string[] | null;
      }) {
        return buildCoachVeeraSignalInterpretRoute(params);
      },
      buildSignalInterpretPrompt(params: {
        signalType?: string | null;
        signalLevel?: string | null;
        signals?: string[] | null;
      }) {
        return buildCoachVeeraSignalInterpretPrompt(params);
      },
    }),
    [],
  );

  return {
    coachLens,
    coachTwin,
    coachMemory,
    coachRuntime,
    coachRuntimeSurface,
    coachAiPageContextData,
    coachRuntimeActions,
    isFetched,
    isError,
  };
}

export type { CoachOverviewSignalType } from "@/lib/behavior/viewModels/buildCoachVeeraEntry";
